import { Injectable } from "@angular/core";
import { ProjectConfigService } from "./project-config.service";
import { XtrasService } from "./xtras.service";
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { PageService } from "./page.service";
import { hexToRgb } from "../utils/color.utils";
import { getEnvironment } from "../environment/environment";

declare let fbq: Function;

@Injectable({
  providedIn: 'root'
})
export class InitializeAppService {

  constructor(
    private xServices: XtrasService,
    private projectConfig: ProjectConfigService,
    private gtmService: GoogleTagManagerService,
    private pageService: PageService,
    private router: Router,
  ) {
  }

  initializeApp() {
    const config = this.projectConfig.getConfig();
    const env = getEnvironment();
    this.pageService.updateMetadata({
      title: config.metadata.title,
      description: config.metadata.description,
      keywords: config.metadata.keywords,
      favicon: config.metadata.favicon
    });

    if(env.gtmId) {
      try {
        this.gtmService.addGtmToDom().then(data => console.log('GTM:', data));
      } catch (error) {
        console.error(error);
      }
    } else {
      console.warn('GTM ID no configurado. Omitiendo proveedor.');
    }

    let { keyMap, zendeskKey, facebookPixelKey } = config;

    if(keyMap) {
      this.loadGoogleMaps(keyMap);
    }

    if(zendeskKey) {
      this.loadZendesk(zendeskKey);
    }

    if(facebookPixelKey) {
      this.loadFacebookPixel(facebookPixelKey);
    }

    this.initializeConfig();

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateAppClass(event.urlAfterRedirects);
      if(facebookPixelKey) {
        this.trackPageView(event.url);
      }
    });
  }

  private loadGoogleMaps(apiKey: string): void {
    this.xServices.loadInlineScriptBody(`
      (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
        v: "weekly",
        key: "${apiKey}"
      });
    `);
  }

  private updateAppClass(url: string) {
    const webApp = document.querySelector('#cy-webapp');
    if (!webApp) return;

    webApp.classList.remove('bg_dark');
    webApp.classList.remove('bg_custom');

    const bgDarkRoutes = this.projectConfig.getConfig().bgDarkRoutes;
    // console.log(bgDarkRoutes);
    const bgCustomRoutes = this.projectConfig.getConfig().bgCustomRoutes;

    setTimeout(() => {
      const bgDark = bgDarkRoutes.some((route: string) => url.includes(route));
      const bgCustom = bgCustomRoutes.some((route: string) => url.includes(route));

      if (bgDark) {
        webApp.classList.add('bg_dark');
      } else if (bgCustom) {
        webApp.classList.add('bg_custom');
      }
    });
  }

  initializeConfig() {
    const globalConfig = this.projectConfig.getConfig();

    const root = document.documentElement;

    const colors = globalConfig?.colors || {};
    const fonts = globalConfig?.fonts || {};

    // Colors
    Object.entries(colors).forEach(([name, value]) => {
      if (value) {
        // Color normal
        root.style.setProperty(`--${name}-color`, value);

        // Versión RGB
        const rgb = hexToRgb(value);
        if (rgb) {
          root.style.setProperty(`--rgb-${name}-color`, rgb);
        }
      }
    });

    // Fonts
    Object.entries(fonts).forEach(([name, value]) => {
      if (value) {
        root.style.setProperty(`--font-${name}`, value);
      }
    });

    if(globalConfig.styles) {
      const styles = document.createElement('style');
      styles.textContent = globalConfig.styles;
      document.head.appendChild(styles);
    }
  }

  private loadZendesk(apiKey: string): void {
    this.xServices.loadScriptBody(`https://static.zdassets.com/ekr/snippet.js?key=${apiKey}`, false, { param: 'id', value: 'ze-snippet' });
  }

  private loadFacebookPixel(apiKey: string): void {
    // Cargar el código base del píxel (sin trackear PageView aún)
    this.xServices.loadInlineScriptHead(`
      !function(f,b,e,v,n,t,s)

      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?

      n.callMethod.apply(n,arguments):n.queue.push(arguments)};

      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';

      n.queue=[];t=b.createElement(e);t.async=!0;

      t.src=v;s=b.getElementsByTagName(e)[0];

      s.parentNode.insertBefore(t,s)}(window, document,'script',

      'https://connect.facebook.net/en_US/fbevents.js');

      fbq('init', '${apiKey}');
      // fbq('track', 'PageView');
    `);

    // Noscript fallback
    this.xServices.loadInlineScriptHead(`
      <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${apiKey}&ev=PageView&noscript=1"
      />
    `, true);

    // Configurar tracking de PageView en cada cambio de ruta
  }

  private trackPageView(url: string): void {
    fbq('track', 'PageView');

    fbq('trackCustom', 'ViewContent', {
      content_name: this.getPageName(url),
      content_category: this.getPageCategory(url),
      page_url: url
    });
  }

  private getPageName(url: string): string {
    // Extraer nombre de la página desde la URL
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(segment => segment.length > 0);

    if (segments.length === 0) return 'Home';
    return segments[segments.length - 1].replace('-', ' ').toUpperCase();
  }

  private getPageCategory(url: string): string {
    // Categorizar las páginas por sección
    if (url.includes('/login')) return 'SignIn';
    if (url.includes('/register')) return 'SignUp';
    if (url.includes('/forgot-password')) return 'ForgotPassword';
    if (url.includes('/payment')) return 'Payment';
    if (url.includes('/success')) return 'PaymentSuccess';
    if (url.includes('/payment-success')) return 'PaymentVoucher';
    if (url.includes('/account')) return 'Account';
    if (url.includes('/tickets-saved')) return 'TicketsSaved';
    if (url.includes('/ticket')) return 'Event';
    if (url.includes('/sections-selector')) return 'SectionsSelector';
    if (url.includes('/search')) return 'Search';
    if (url.includes('/privacy-policy')) return 'PrivacyPolicy';
    if (url === '/' || url === '' || url === '/home') return 'Home';
    return 'General';
  }
}
